import { z } from 'zod';
import { EntityKind } from './devices.js';
// ---- Monitoring (compliance demo C1) ---------------------------------------
const IsoDate = z.string().date('valid YYYY-MM-DD date required');
export const MonitoringOperator = z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'exists', 'missing']);
const FieldPath = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/, 'dotted field path required, e.g. attributes.grid_connected');
const Scalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
/** A condition on one field of the register record as it stood on the run's as-of date. */
export const MonitoringCondition = z
    .object({ field: FieldPath, op: MonitoringOperator, value: z.unknown().optional() })
    .strict()
    .superRefine((c, ctx) => {
    if (c.op === 'exists' || c.op === 'missing') {
        if (c.value !== undefined)
            ctx.addIssue({ code: 'custom', path: ['value'], message: `${c.op} takes no value` });
    }
    else if (c.op === 'in') {
        if (!z.array(Scalar).min(1).safeParse(c.value).success)
            ctx.addIssue({ code: 'custom', path: ['value'], message: 'in needs a non-empty array of scalars' });
    }
    else if (!Scalar.safeParse(c.value).success) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: `${c.op} needs a scalar value` });
    }
});
/** A JSON predicate over a register record: a condition, or all/any/not of predicates. A record matching it is a hit. */
export const MonitoringPredicate = z.lazy(() => z.union([
    z.object({ all: z.array(MonitoringPredicate).min(1) }).strict(),
    z.object({ any: z.array(MonitoringPredicate).min(1) }).strict(),
    z.object({ not: MonitoringPredicate }).strict(),
    MonitoringCondition,
]));
export const MonitoringRule = z.object({
    /** Surrogate id of this rule version. @metadata technical @source compliance.monitoring_rules.id */
    id: z.number().int(),
    /** Stable code of the rule across versions. @metadata business @source compliance.monitoring_rules.code */
    code: z.string(),
    /** Version of the rule. @metadata business @source compliance.monitoring_rules.version */
    version: z.number().int(),
    /** Short title of the rule. @metadata business @source compliance.monitoring_rules.title */
    title: z.string(),
    /** What the rule looks for and why. @metadata business @source compliance.monitoring_rules.description */
    description: z.string(),
    /** Kind of register record the rule tests: model, unit or installation. @metadata business @source compliance.monitoring_rules.entity_kind */
    entityKind: EntityKind,
    /** Device class the rule is limited to; null for every class. @metadata business @source compliance.monitoring_rules.device_class */
    deviceClass: z.string().nullable(),
    /** Condition a record must meet to be a hit: field comparisons combined with all, any and not. @metadata business @source compliance.monitoring_rules.predicate */
    predicate: MonitoringPredicate,
    /** When this version starts to apply. @metadata business @source compliance.monitoring_rules.valid_from */
    validFrom: z.string(),
    /** When this version stopped applying; null while it is current. @metadata business @source compliance.monitoring_rules.valid_to */
    validTo: z.string().nullable(),
    /** Why the rule was created or changed. @metadata business @source compliance.monitoring_rules.change_reason */
    changeReason: z.string(),
    /** Display name of the user who created this version. @metadata technical @source iam.users.display_name via compliance.monitoring_rules.created_by_user_id @transform left join */
    createdByName: z.string().nullable(),
    /** When this version was recorded. @metadata technical @source compliance.monitoring_rules.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const MonitoringRuleList = z.object({ items: z.array(MonitoringRule) });
/** Publishes a new version of a monitoring rule (version 1 if the code is new). */
export const MonitoringRuleInput = z.object({
    code: z.string().regex(/^[A-Z][A-Z0-9-]{2,39}$/, 'upper-case code, e.g. MR-BAT-GRID'),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    entityKind: EntityKind,
    deviceClass: z.string().min(1).nullable().default(null),
    predicate: MonitoringPredicate,
    validFrom: IsoDate,
    changeReason: z.string().trim().min(1).max(500),
});
export const MonitoringRunInput = z.object({
    ruleCode: z.string().min(1),
    /** The business date checked: facts true at the end of this day (UTC). */
    asOf: IsoDate,
    /** What CER had recorded by this instant. Defaults to now; pass an earlier run's knownAt to reproduce it. */
    knownAt: z.string().datetime({ offset: true }).optional(),
});
export const MonitoringHit = z.object({
    id: z.number().int(),
    entityKind: EntityKind,
    entityId: z.string(),
    historyVersionId: z.number().int(),
    entityVersion: z.number().int(),
    label: z.string(),
    responsibleParticipantId: z.string().nullable(),
    responsibleParticipantName: z.string().nullable(),
    evidence: z.object({
        validFrom: z.string(),
        recordedAt: z.string(),
        ruleVersionApplied: z.number().int().nullable(),
        fields: z.record(z.unknown()),
    }),
});
export const MonitoringRunSummary = z.object({
    id: z.number().int(),
    ruleId: z.number().int(),
    ruleCode: z.string(),
    ruleVersion: z.number().int(),
    ruleTitle: z.string(),
    asOf: z.string(),
    knownAt: z.string(),
    population: z.number().int(),
    hitCount: z.number().int(),
    runAt: z.string(),
    runByName: z.string().nullable(),
});
const HitRef = z.object({ entityKind: EntityKind, entityId: z.string(), label: z.string() });
export const MonitoringRun = MonitoringRunSummary.extend({
    rule: MonitoringRule,
    hits: z.array(MonitoringHit),
    /** The previous run of the same rule code, and how this run's hits differ from it. */
    previous: z
        .object({ run: MonitoringRunSummary, resolved: z.array(HitRef), added: z.array(HitRef) })
        .nullable(),
});
export const MonitoringRunList = z.object({ items: z.array(MonitoringRunSummary) });
/**
 * A monitoring run started in the background (POST /compliance/monitoring/jobs).
 * `scanned` counts history records read so far and `total` is the register's
 * record count for the rule's kind when the job started, so the bar is
 * approximate; `population` and `hitCount` are the in-scope records and hits
 * so far. A completed job links to its run; a failed one says why.
 */
export const MonitoringJob = z.object({
    id: z.number().int(),
    ruleCode: z.string(),
    asOf: z.string(),
    knownAt: z.string().nullable(),
    status: z.enum(['running', 'completed', 'failed']),
    entityKind: EntityKind,
    total: z.number().int(),
    scanned: z.number().int(),
    population: z.number().int(),
    hitCount: z.number().int(),
    runId: z.number().int().nullable(),
    error: z.string().nullable(),
    requestedByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const MonitoringJobList = z.object({ items: z.array(MonitoringJob) });
export const MonitoringRunQuery = z.object({ ruleCode: z.string().optional() });
// ---- Risk view (compliance demo C2) -----------------------------------------
/** Where a risk signal came from. Each is read live from its source; the risk view stores nothing. */
export const RiskSource = z.enum(['monitoring_hit', 'incident', 'recall_unit', 'reconciliation', 'access_denied']);
const PerSource = z.object({
    monitoring_hit: z.number().int(),
    incident: z.number().int(),
    recall_unit: z.number().int(),
    reconciliation: z.number().int(),
    access_denied: z.number().int(),
});
/** The record a signal was read from, e.g. the monitoring run that retained a hit. */
export const RiskSourceRecord = z.object({
    /** Kind of record the signal was read from. @metadata technical */
    kind: z.enum(['monitoring_run', 'incident', 'recall', 'reconciliation_run', 'audit_event']),
    /** Id of that record. @metadata technical */
    id: z.string(),
    /** Human-readable label of that record. @metadata technical */
    label: z.string(),
});
export const RiskSignal = z.object({
    source: RiskSource,
    /** Id of the signal row in its source table (hit, incident, affected unit, difference, audit event). */
    sourceId: z.string(),
    title: z.string(),
    detail: z.string(),
    observedAt: z.string(),
    weight: z.number().int(),
    sourceRecord: RiskSourceRecord,
    registerRecord: z.object({ kind: EntityKind, id: z.string(), label: z.string() }).nullable(),
});
export const RiskRow = z.object({
    /** Null for signals that cannot be tied to a registered participant (e.g. a partner record missing here). */
    participantId: z.string().nullable(),
    participantName: z.string().nullable(),
    score: z.number().int(),
    counts: PerSource,
    /** Every live signal behind the score. `signals` lists the heaviest and newest of them, at most 200. */
    signalCount: z.number().int(),
    signals: z.array(RiskSignal),
});
export const RiskView = z.object({
    generatedAt: z.string(),
    weights: PerSource,
    /** access.denied events are counted from this instant (a rolling window). */
    accessDeniedSince: z.string(),
    rows: z.array(RiskRow),
});
// ---- Cases (compliance demo C3) ---------------------------------------------
export const CaseStatus = z.enum(['open', 'assessing', 'acting', 'closed']);
export const CaseOutcome = z.enum(['no_further_action', 'remediated', 'enforcement']);
export const CaseLinkKind = z.enum(['monitoring_hit', 'incident', 'recall_unit', 'reconciliation', 'access_denied', 'inspection', 'audit', 'enforcement_action']);
export const CaseEventKind = z.enum(['opened', 'status', 'note', 'acknowledged', 'evidence', 'correction', 'owner', 'inspection', 'audit', 'enforcement']);
/** One append-only step on a case. Corrections never change the row they correct; they point at it. */
export const CaseEvent = z.object({
    /** Surrogate id of the event. @metadata technical @source compliance.case_events.id */
    id: z.number().int(),
    /** Kind of step: opened, status, note, acknowledged, evidence, correction, owner, inspection, audit or enforcement. @metadata business @source compliance.case_events.kind */
    kind: CaseEventKind,
    /** Case status before a status change. @metadata business @source compliance.case_events.from_status */
    fromStatus: CaseStatus.nullable(),
    /** Case status after a status change. @metadata business @source compliance.case_events.to_status */
    toStatus: CaseStatus.nullable(),
    /** Outcome recorded when the case closed. @metadata business @source compliance.case_events.outcome */
    outcome: CaseOutcome.nullable(),
    /** Note recorded with the step. @metadata business @source compliance.case_events.note */
    note: z.string().nullable(),
    /** Reference to evidence added in the step. @metadata business @source compliance.case_events.evidence_ref */
    evidenceRef: z.string().nullable(),
    /** Id of the earlier event this one corrects. @metadata technical @source compliance.case_events.corrects_event_id */
    correctsEventId: z.number().int().nullable(),
    /** Ids of later events that correct this one, oldest first. @metadata technical @source derived: compliance.case_events.id of events whose corrects_event_id is this event */
    correctedBy: z.array(z.number().int()),
    /** Reason given for a correction or reassignment. @metadata business @source compliance.case_events.reason */
    reason: z.string().nullable(),
    /** Previous case owner, on owner events. @metadata technical @source iam.users.display_name via compliance.case_events.from_owner_user_id @transform left join */
    fromOwnerName: z.string().nullable(),
    /** New case owner, on owner events. @metadata technical @source iam.users.display_name via compliance.case_events.to_owner_user_id @transform left join */
    toOwnerName: z.string().nullable(),
    /** The inspection an inspection event is about. @metadata technical @source compliance.case_events.inspection_id */
    inspectionId: z.string().nullable(),
    /** The audit an audit event is about. @metadata technical @source compliance.case_events.audit_id */
    auditId: z.string().nullable(),
    /** The follow-up a follow-up completion is about. @metadata technical @source compliance.case_events.audit_finding_id */
    auditFindingId: z.number().int().nullable(),
    /** The enforcement action an enforcement event is about. @metadata technical @source compliance.case_events.enforcement_action_id */
    enforcementActionId: z.string().nullable(),
    /** Whether the subject participant can see the event. @metadata technical @source compliance.case_events.shared */
    shared: z.boolean(),
    /** User who recorded the step. @metadata technical @source compliance.case_events.actor_id @transform null for a CER actor when read with participant scope */
    actorId: z.string().nullable(),
    /** Display name of that user. @metadata technical @source iam.users.display_name via compliance.case_events.actor_id @transform left join */
    actorName: z.string().nullable(),
    /** Participant the user acted for; null for CER. @metadata technical @source compliance.case_events.actor_participant_id */
    actorParticipantId: z.string().nullable(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via compliance.case_events.actor_participant_id @transform left join */
    actorParticipantName: z.string().nullable(),
    /** When the step was recorded. @metadata technical @source compliance.case_events.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
/** What a case link points at: a risk signal's source record, or a compliance record (an inspection) linked later. */
export const CaseLinkRecord = RiskSourceRecord.extend({
    /** Kind of record the link was read from, including inspection, audit and enforcement_action. @metadata technical */
    kind: z.enum([...RiskSourceRecord.shape.kind.options, 'inspection', 'audit', 'enforcement_action']),
});
const CaseLinkRegisterRecord = z.object({
    /** Kind of register record: model, unit or installation. @metadata technical */
    kind: EntityKind,
    /** Id of the register record. @metadata technical */
    id: z.string(),
    /** Human-readable label of the register record. @metadata technical */
    label: z.string(),
});
/** A risk signal (or later an inspection, audit or action) linked to a case, snapshotted when linked. */
export const CaseLink = z.object({
    /** Surrogate id of the link. @metadata technical @source compliance.case_links.id */
    id: z.number().int(),
    /** What was linked: a risk signal kind, inspection, audit or enforcement_action. @metadata business @source compliance.case_links.kind */
    kind: CaseLinkKind,
    /** Id of the linked signal or record. @metadata technical @source compliance.case_links.ref_id */
    refId: z.string(),
    /** Title of the linked signal, snapshotted when linked. @metadata business @source compliance.case_links.title */
    title: z.string(),
    /** Detail of the linked signal, snapshotted when linked. @metadata business @source compliance.case_links.detail */
    detail: z.string(),
    /** Risk weight of the signal, if it has one. @metadata business @source compliance.case_links.weight */
    weight: z.number().int().nullable(),
    /** When the signal was observed. @metadata business @source compliance.case_links.observed_at @transform timestamptz as an ISO 8601 UTC string */
    observedAt: z.string(),
    /** The record the signal was read from. @metadata technical @source compliance.case_links.source_record */
    sourceRecord: CaseLinkRecord,
    /** The register record the signal is about, if any. @metadata technical @source compliance.case_links.register_record */
    registerRecord: CaseLinkRegisterRecord.nullable(),
    /** When the link was made. @metadata technical @source compliance.case_links.linked_at @transform timestamptz as an ISO 8601 UTC string */
    linkedAt: z.string(),
});
export const CaseSummary = z.object({
    /** Surrogate id of the case. @metadata technical @source compliance.cases.id */
    id: z.string(),
    /** Case status: open, assessing, acting or closed. @metadata business @source compliance.cases.status */
    status: CaseStatus,
    /** Short title of the case. @metadata business @source compliance.cases.title */
    title: z.string(),
    /** Participant the case is about. @metadata technical @source compliance.cases.subject_participant_id */
    subjectParticipantId: z.string(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via compliance.cases.subject_participant_id @transform joined from the participant record */
    subjectParticipantName: z.string(),
    /** Unit the case is about, if any. @metadata technical @source compliance.cases.subject_unit_id */
    subjectUnitId: z.string().nullable(),
    /** Serial number of that unit. @metadata business @source register.device_units.serial via compliance.cases.subject_unit_id @transform left join */
    subjectUnitSerial: z.string().nullable(),
    /** CER officer who owns the case. @metadata technical @source compliance.cases.owner_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    ownerUserId: z.string().nullable(),
    /** Display name of the case owner. @metadata technical @source iam.users.display_name via compliance.cases.owner_user_id @transform left join */
    ownerName: z.string().nullable(),
    /** Date the case was opened. @metadata business @source compliance.cases.opened_on */
    openedOn: z.string(),
    /** Business days allowed to resolve the case. @metadata business @source compliance.cases.sla_business_days */
    slaBusinessDays: z.number().int(),
    /** Date the case is due. @metadata business @source compliance.cases.due_on */
    dueOn: z.string(),
    /** Not closed and past its due date (Sydney time). @metadata business @source derived: compliance.cases.status and due_on compared with the current date @transform computed when the snapshot is taken */
    overdue: z.boolean(),
    /** When the subject participant acknowledged the case. @metadata business @source derived: compliance.case_events.created_at of the case's acknowledged event @transform timestamptz as an ISO 8601 UTC string */
    acknowledgedAt: z.string().nullable(),
    /** Outcome once closed: no_further_action, remediated or enforcement. @metadata business @source compliance.cases.outcome */
    outcome: CaseOutcome.nullable(),
    /** When the case was closed. @metadata business @source compliance.cases.closed_at @transform timestamptz as an ISO 8601 UTC string */
    closedAt: z.string().nullable(),
    /** Number of signals and records linked to the case. @metadata business @source derived: count(compliance.case_links) for the case */
    linkCount: z.number().int(),
    /** When the case was created. @metadata technical @source compliance.cases.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the case was last changed. @metadata technical @source compliance.cases.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
// ---- Inspections (compliance demo C4) ---------------------------------------
export const InspectionStatus = z.enum(['scheduled', 'completed', 'cancelled']);
export const InspectionOutcome = z.enum(['compliant', 'non_compliant']);
/** One item checked on site, e.g. `grid_connected` observed `false`, not compliant. */
export const InspectionFinding = z
    .object({
    /** Item checked on site, e.g. grid_connected. @metadata business */
    item: z.string().trim().min(1).max(100),
    /** Value observed. @metadata business */
    observed: z.string().trim().min(1).max(500),
    /** Whether the observation is compliant. @metadata business */
    compliant: z.boolean(),
    /** Inspector's note on the item. @metadata business */
    note: z.string().trim().max(2000).optional(),
})
    .strict();
export const Inspection = z.object({
    /** Surrogate id of the inspection. @metadata technical @source compliance.inspections.id */
    id: z.string(),
    /** Id of the compliance case it belongs to. @metadata technical @source compliance.inspections.case_id */
    caseId: z.string(),
    /** Title of that case. @metadata business @source compliance.cases.title via compliance.inspections.case_id @transform joined from the case */
    caseTitle: z.string(),
    /** Participant the inspection is about. @metadata technical @source compliance.inspections.subject_participant_id */
    subjectParticipantId: z.string(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via compliance.inspections.subject_participant_id @transform joined from the participant record */
    subjectParticipantName: z.string(),
    /** Unit inspected, if any. @metadata technical @source compliance.inspections.unit_id */
    unitId: z.string().nullable(),
    /** Serial number of that unit. @metadata business @source register.device_units.serial via compliance.inspections.unit_id @transform left join */
    unitSerial: z.string().nullable(),
    /** Installation inspected, if any. @metadata technical @source compliance.inspections.installation_id */
    installationId: z.string().nullable(),
    /** NMI of that installation. @metadata business @source register.installations.nmi via compliance.inspections.installation_id @transform left join */
    installationNmi: z.string().nullable(),
    /** Inspection status: scheduled, completed or cancelled. @metadata business @source compliance.inspections.status */
    status: InspectionStatus,
    /** Date the inspection is scheduled for. @metadata business @source compliance.inspections.scheduled_for */
    scheduledFor: z.string(),
    /** Why the inspection was scheduled. @metadata business @source compliance.inspections.reason */
    reason: z.string(),
    /** Date the inspection took place. @metadata business @source compliance.inspections.inspected_on */
    inspectedOn: z.string().nullable(),
    /** Items checked on site and what was observed. @metadata business @source compliance.inspections.findings */
    findings: z.array(InspectionFinding),
    /** Reference to the inspection evidence. @metadata business @source compliance.inspections.evidence_ref */
    evidenceRef: z.string().nullable(),
    /** Outcome: compliant or non_compliant. @metadata business @source compliance.inspections.outcome */
    outcome: InspectionOutcome.nullable(),
    /** Summary of the outcome. @metadata business @source compliance.inspections.outcome_summary */
    outcomeSummary: z.string().nullable(),
    /** When the inspection was recorded as completed. @metadata business @source compliance.inspections.completed_at @transform timestamptz as an ISO 8601 UTC string */
    completedAt: z.string().nullable(),
    /** Why the inspection was cancelled. @metadata business @source compliance.inspections.cancel_reason */
    cancelReason: z.string().nullable(),
    /** When the inspection was cancelled. @metadata business @source compliance.inspections.cancelled_at @transform timestamptz as an ISO 8601 UTC string */
    cancelledAt: z.string().nullable(),
    /** CER officer who scheduled the inspection. @metadata technical @source iam.users.display_name via compliance.inspections.scheduled_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    scheduledByName: z.string().nullable(),
    /** CER officer who completed the inspection. @metadata technical @source iam.users.display_name via compliance.inspections.completed_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    completedByName: z.string().nullable(),
    /** When the inspection was created. @metadata technical @source compliance.inspections.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const InspectionList = z.object({ items: z.array(Inspection) });
export const InspectionQuery = z.object({
    status: InspectionStatus.optional(),
    participantId: z.string().optional(),
    caseId: z.string().optional(),
});
/** Schedules an inspection on an open case: of the subject participant, optionally one of its units or installations. */
export const InspectionSchedule = z
    .object({
    caseId: z.string().min(1),
    unitId: z.string().min(1).optional(),
    installationId: z.string().min(1).optional(),
    scheduledFor: IsoDate,
    reason: z.string().trim().min(3).max(2000),
})
    .strict();
/** Records what was found; completes the inspection, which is final from then on. */
export const InspectionComplete = z
    .object({
    inspectedOn: IsoDate,
    findings: z.array(InspectionFinding).min(1).max(50),
    evidenceRef: z.string().trim().min(1).max(500).optional(),
    outcome: InspectionOutcome,
    summary: z.string().trim().min(3).max(5000),
})
    .strict();
export const InspectionCancel = z.object({ reason: z.string().trim().min(3).max(2000) }).strict();
/** An installation an inspection on this participant may visit: one it installed, or of a unit it owns or manufactures. */
export const CaseSubjectInstallation = z.object({ id: z.string(), nmi: z.string(), unitId: z.string(), unitSerial: z.string(), status: z.string(), commissionedOn: z.string() });
export const CaseSubjectInstallationList = z.object({ items: z.array(CaseSubjectInstallation) });
// ---- Obligations (compliance demo C6) --------------------------------------
const ObligationRuleSet = z.object({
    /** Id of the device class rule version. @metadata technical */
    id: z.number().int(),
    /** Device class of the rule set. @metadata business */
    deviceClass: z.string(),
    /** Version of the rule set. @metadata business */
    version: z.number().int(),
    /** Date the rule set version applies from. @metadata business */
    validFrom: z.string(),
    /** Date it stopped applying. @metadata business */
    validTo: z.string().nullable(),
});
export const Obligation = z.object({
    /** Surrogate id of this obligation version. @metadata technical @source compliance.obligations.id */
    id: z.number().int(),
    /** Stable code of the obligation across versions. @metadata business @source compliance.obligations.code */
    code: z.string(),
    /** Version of the obligation. @metadata business @source compliance.obligations.version */
    version: z.number().int(),
    /** Short title of the obligation. @metadata business @source compliance.obligations.title */
    title: z.string(),
    /** Text of the obligation. @metadata business @source compliance.obligations.body */
    text: z.string(),
    /** Date this version applies from. @metadata business @source compliance.obligations.valid_from */
    validFrom: z.string(),
    /** Date this version stopped applying; null while it is current. @metadata business @source compliance.obligations.valid_to */
    validTo: z.string().nullable(),
    /** The device rule set that gives the obligation effect, if any. @metadata business @source compliance.obligations.rule_set_id @source register.device_class_rules.device_class via compliance.obligations.rule_set_id @transform joined from the rule set version; null when there is none */
    ruleSet: ObligationRuleSet.nullable(),
    /** Why the obligation was created or changed. @metadata business @source compliance.obligations.change_reason */
    changeReason: z.string(),
    /** Display name of the user who created this version. @metadata technical @source iam.users.display_name via compliance.obligations.created_by_user_id @transform left join */
    createdByName: z.string().nullable(),
    /** When this version was recorded. @metadata technical @source compliance.obligations.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const ObligationList = z.object({ items: z.array(Obligation) });
/** Publishes a new version of an obligation (version 1 if the code is new). */
export const ObligationInput = z
    .object({
    code: z.string().regex(/^[A-Z][A-Z0-9-]{2,39}$/, 'upper-case code, e.g. OBL-BAT-GRID'),
    title: z.string().trim().min(3).max(200),
    text: z.string().trim().min(3).max(5000),
    validFrom: IsoDate,
    ruleSetId: z.number().int().positive().nullable().default(null),
    changeReason: z.string().trim().min(3).max(500),
})
    .strict();
// ---- Audits and follow-ups (compliance demo C5) -----------------------------
export const AuditStatus = z.enum(['planned', 'conducted', 'closed', 'cancelled']);
export const FollowUpStatus = z.enum(['open', 'completed']);
/** A finding as recorded when the audit is conducted: the obligation it relates to and the follow-up action required by a date. */
export const AuditFindingInput = z
    .object({
    finding: z.string().trim().min(3).max(2000),
    /** The obligation version the finding relates to; it must be in force during the audit period. */
    obligationId: z.number().int().positive(),
    followUpAction: z.string().trim().min(3).max(2000),
    dueOn: IsoDate,
})
    .strict();
const FindingObligation = z.object({
    /** Id of the obligation version relied on. @metadata technical */
    id: z.number().int(),
    /** Code of the obligation. @metadata business */
    code: z.string(),
    /** Version of the obligation. @metadata business */
    version: z.number().int(),
    /** Title of the obligation. @metadata business */
    title: z.string(),
});
export const AuditFinding = z.object({
    /** Surrogate id of the finding. @metadata technical @source compliance.audit_findings.id */
    id: z.number().int(),
    /** Position of the finding in the audit. @metadata business @source compliance.audit_findings.seq */
    seq: z.number().int(),
    /** What the audit found. @metadata business @source compliance.audit_findings.finding */
    finding: z.string(),
    /** CODE vN of the obligation; free text on findings recorded before obligations were versioned. @metadata business @source compliance.audit_findings.obligation_ref */
    obligationRef: z.string(),
    /** The obligation version relied on; null on findings recorded before obligations were versioned. @metadata business @source compliance.audit_findings.obligation_id @source compliance.obligations.code via compliance.audit_findings.obligation_id @transform joined from the obligation version */
    obligation: FindingObligation.nullable(),
    /** Action the participant must take. @metadata business @source compliance.audit_findings.follow_up_action */
    followUpAction: z.string(),
    /** Date the follow-up is due. @metadata business @source compliance.audit_findings.due_on */
    dueOn: z.string(),
    /** Follow-up status: open or completed. @metadata business @source compliance.audit_findings.status */
    status: FollowUpStatus,
    /** Open with its due date before today (Sydney time). @metadata business @source derived: compliance.audit_findings.status and due_on compared with the current date @transform computed when the snapshot is taken */
    overdue: z.boolean(),
    /** Note recorded when the follow-up was completed. @metadata business @source compliance.audit_findings.completion_note */
    completionNote: z.string().nullable(),
    /** Reference to evidence of completion. @metadata business @source compliance.audit_findings.completion_evidence_ref */
    completionEvidenceRef: z.string().nullable(),
    /** When the follow-up was completed. @metadata business @source compliance.audit_findings.completed_at @transform timestamptz as an ISO 8601 UTC string */
    completedAt: z.string().nullable(),
    /** User who completed the follow-up. @metadata technical @source iam.users.display_name via compliance.audit_findings.completed_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    completedByName: z.string().nullable(),
});
export const Audit = z.object({
    /** Surrogate id of the audit. @metadata technical @source compliance.audits.id */
    id: z.string(),
    /** Id of the compliance case it belongs to. @metadata technical @source compliance.audits.case_id */
    caseId: z.string(),
    /** Title of that case. @metadata business @source compliance.cases.title via compliance.audits.case_id @transform joined from the case */
    caseTitle: z.string(),
    /** Participant the audit is about. @metadata technical @source compliance.audits.subject_participant_id */
    subjectParticipantId: z.string(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via compliance.audits.subject_participant_id @transform joined from the participant record */
    subjectParticipantName: z.string(),
    /** Audit status: planned, conducted, closed or cancelled. @metadata business @source compliance.audits.status */
    status: AuditStatus,
    /** What the audit covers. @metadata business @source compliance.audits.scope */
    scope: z.string(),
    /** Start of the period audited. @metadata business @source compliance.audits.period_from */
    periodFrom: z.string(),
    /** End of the period audited. @metadata business @source compliance.audits.period_to */
    periodTo: z.string(),
    /** Date the audit is planned for. @metadata business @source compliance.audits.planned_for */
    plannedFor: z.string(),
    /** Why the audit was planned. @metadata business @source compliance.audits.reason */
    reason: z.string(),
    /** Date the audit was conducted. @metadata business @source compliance.audits.conducted_on */
    conductedOn: z.string().nullable(),
    /** Summary of the audit as conducted. @metadata business @source compliance.audits.conduct_summary */
    conductSummary: z.string().nullable(),
    /** Reference to the audit evidence. @metadata business @source compliance.audits.evidence_ref */
    evidenceRef: z.string().nullable(),
    /** When the conduct was recorded. @metadata technical @source compliance.audits.conducted_at @transform timestamptz as an ISO 8601 UTC string */
    conductedAt: z.string().nullable(),
    /** Summary recorded when the audit closed. @metadata business @source compliance.audits.close_summary */
    closeSummary: z.string().nullable(),
    /** When the audit was closed. @metadata business @source compliance.audits.closed_at @transform timestamptz as an ISO 8601 UTC string */
    closedAt: z.string().nullable(),
    /** Why the audit was cancelled. @metadata business @source compliance.audits.cancel_reason */
    cancelReason: z.string().nullable(),
    /** When the audit was cancelled. @metadata business @source compliance.audits.cancelled_at @transform timestamptz as an ISO 8601 UTC string */
    cancelledAt: z.string().nullable(),
    /** The audit's findings and follow-up actions. @metadata business @source derived: compliance.audit_findings rows for the audit */
    findings: z.array(AuditFinding),
    /** CER officer who planned the audit. @metadata technical @source iam.users.display_name via compliance.audits.planned_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    plannedByName: z.string().nullable(),
    /** CER officer who recorded the conduct. @metadata technical @source iam.users.display_name via compliance.audits.conducted_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    conductedByName: z.string().nullable(),
    /** CER officer who closed the audit. @metadata technical @source iam.users.display_name via compliance.audits.closed_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    closedByName: z.string().nullable(),
    /** When the audit was created. @metadata technical @source compliance.audits.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const AuditList = z.object({ items: z.array(Audit) });
export const AuditQuery = z.object({
    status: AuditStatus.optional(),
    participantId: z.string().optional(),
    caseId: z.string().optional(),
});
/** Plans an audit of an open case's subject participant over a period. */
export const AuditPlan = z
    .object({
    caseId: z.string().min(1),
    scope: z.string().trim().min(3).max(2000),
    periodFrom: IsoDate,
    periodTo: IsoDate,
    plannedFor: IsoDate,
    reason: z.string().trim().min(3).max(2000),
})
    .strict()
    .refine((a) => a.periodTo >= a.periodFrom, { path: ['periodTo'], message: 'The period must end on or after it starts' });
/** Records the audit as conducted, with its findings; final from then on. */
export const AuditConduct = z
    .object({
    conductedOn: IsoDate,
    summary: z.string().trim().min(3).max(5000),
    evidenceRef: z.string().trim().min(1).max(500).optional(),
    findings: z.array(AuditFindingInput).max(50),
})
    .strict();
export const AuditClose = z.object({ summary: z.string().trim().min(3).max(5000) }).strict();
export const AuditCancel = z.object({ reason: z.string().trim().min(3).max(2000) }).strict();
/** A row of the follow-up register: an audit finding's follow-up action with where it came from. */
export const FollowUp = AuditFinding.extend({
    auditId: z.string(),
    auditScope: z.string(),
    auditStatus: AuditStatus,
    caseId: z.string(),
    caseTitle: z.string(),
    subjectParticipantId: z.string(),
    subjectParticipantName: z.string(),
});
export const FollowUpList = z.object({ items: z.array(FollowUp) });
export const FollowUpQuery = z.object({
    participantId: z.string().optional(),
    status: FollowUpStatus.optional(),
    overdue: z.enum(['true', 'false']).optional(),
    auditId: z.string().optional(),
});
export const FollowUpComplete = z
    .object({
    note: z.string().trim().min(3).max(5000),
    evidenceRef: z.string().trim().min(1).max(500).optional(),
})
    .strict();
export const EnforcementActionType = z.enum(['formal_warning', 'infringement_notice', 'enforceable_undertaking', 'compliance_direction', 'suspension']);
export const EnforcementActionStatus = z.enum(['draft', 'issued', 'withdrawn', 'closed']);
/** How an issued action ended when it was closed. */
export const EnforcementCloseOutcome = z.enum(['complied', 'not_complied']);
/** Evidence an action relies on: a completed inspection, an audit finding, or a participant evidence entry on the case. */
export const EnforcementEvidenceKind = z.enum(['inspection', 'audit_finding', 'case_evidence']);
export const EnforcementEvidenceRef = z.object({ kind: EnforcementEvidenceKind, id: z.string().trim().min(1).max(100) }).strict();
export const EnforcementEvidence = z.object({
    /** Kind of evidence: inspection, audit_finding or case_evidence. @metadata business */
    kind: EnforcementEvidenceKind,
    /** Id of the evidence record. @metadata technical */
    id: z.string(),
    /** Description of the evidence. @metadata business */
    label: z.string(),
});
const EnforcementObligation = z.object({
    /** Id of the obligation version. @metadata technical */
    id: z.number().int(),
    /** Code of the obligation. @metadata business */
    code: z.string(),
    /** Version of the obligation. @metadata business */
    version: z.number().int(),
    /** Title of the obligation. @metadata business */
    title: z.string(),
    /** Date the version applies from. @metadata business */
    validFrom: z.string(),
    /** Date it stopped applying. @metadata business */
    validTo: z.string().nullable(),
});
export const EnforcementAction = z.object({
    /** Surrogate id of the enforcement action. @metadata technical @source compliance.enforcement_actions.id */
    id: z.string(),
    /** Id of the compliance case it belongs to. @metadata technical @source compliance.enforcement_actions.case_id */
    caseId: z.string(),
    /** Title of that case. @metadata business @source compliance.cases.title via compliance.enforcement_actions.case_id @transform joined from the case */
    caseTitle: z.string(),
    /** Participant the enforcement action is about. @metadata technical @source compliance.enforcement_actions.subject_participant_id */
    subjectParticipantId: z.string(),
    /** Legal name of that participant. @metadata business @source register.participants.legal_name via compliance.enforcement_actions.subject_participant_id @transform joined from the participant record */
    subjectParticipantName: z.string(),
    /** Type: formal_warning, infringement_notice, enforceable_undertaking, compliance_direction or suspension. @metadata business @source compliance.enforcement_actions.type */
    type: EnforcementActionType,
    /** Status: draft, issued, withdrawn or closed. @metadata business @source compliance.enforcement_actions.status */
    status: EnforcementActionStatus,
    /** Date of the conduct the action responds to. @metadata business @source compliance.enforcement_actions.conduct_on */
    conductOn: z.string(),
    /** The obligation version in force on the conduct date, resolved by the server. @metadata business @source compliance.enforcement_actions.obligation_id @source compliance.enforcement_actions.obligation_version @source compliance.obligations.code via compliance.enforcement_actions.obligation_id @transform joined from the obligation version */
    obligation: EnforcementObligation,
    /** Evidence relied on, snapshotted when drafted. @metadata business @source compliance.enforcement_actions.evidence */
    evidence: z.array(EnforcementEvidence),
    /** Reasons for the action. @metadata business @source compliance.enforcement_actions.reasons */
    reasons: z.string(),
    /** CER decision-maker. @metadata technical @source compliance.enforcement_actions.decision_maker_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    decisionMakerUserId: z.string().nullable(),
    /** Display name of the decision-maker. @metadata business @source iam.users.display_name via compliance.enforcement_actions.decision_maker_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    decisionMakerName: z.string().nullable(),
    /** Date the action was decided. @metadata business @source compliance.enforcement_actions.decided_on */
    decidedOn: z.string().nullable(),
    /** When the action was issued. @metadata business @source compliance.enforcement_actions.issued_at @transform timestamptz as an ISO 8601 UTC string */
    issuedAt: z.string().nullable(),
    /** Why the action was withdrawn. @metadata business @source compliance.enforcement_actions.withdraw_reason */
    withdrawReason: z.string().nullable(),
    /** When the action was withdrawn. @metadata business @source compliance.enforcement_actions.withdrawn_at @transform timestamptz as an ISO 8601 UTC string */
    withdrawnAt: z.string().nullable(),
    /** Outcome on closing: complied or not_complied. @metadata business @source compliance.enforcement_actions.close_outcome */
    closeOutcome: EnforcementCloseOutcome.nullable(),
    /** Reason recorded on closing. @metadata business @source compliance.enforcement_actions.close_reason */
    closeReason: z.string().nullable(),
    /** When the action was closed. @metadata business @source compliance.enforcement_actions.closed_at @transform timestamptz as an ISO 8601 UTC string */
    closedAt: z.string().nullable(),
    /** CER officer who drafted the action. @metadata technical @source iam.users.display_name via compliance.enforcement_actions.drafted_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    draftedByName: z.string().nullable(),
    /** CER officer who withdrew the action. @metadata technical @source iam.users.display_name via compliance.enforcement_actions.withdrawn_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    withdrawnByName: z.string().nullable(),
    /** CER officer who closed the action. @metadata technical @source iam.users.display_name via compliance.enforcement_actions.closed_by_user_id @transform null when read with participant scope (CER staff records are hidden from participants) */
    closedByName: z.string().nullable(),
    /** When the action was created. @metadata technical @source compliance.enforcement_actions.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const EnforcementActionList = z.object({ items: z.array(EnforcementAction) });
export const EnforcementActionQuery = z.object({
    status: EnforcementActionStatus.optional(),
    participantId: z.string().optional(),
    caseId: z.string().optional(),
});
/**
 * Drafts an action on a case that is `acting`. The client names the obligation by code; the server picks the
 * version in force on the conduct date.
 */
export const EnforcementActionDraft = z
    .object({
    caseId: z.string().min(1),
    type: EnforcementActionType,
    conductOn: IsoDate,
    obligationCode: z.string().trim().min(1).max(100),
    evidence: z.array(EnforcementEvidenceRef).min(1).max(20),
    decisionMakerUserId: z.string().uuid(),
    reasons: z.string().trim().min(10).max(10000),
})
    .strict();
/** The named decision-maker issues the action with the date of the decision. */
export const EnforcementActionIssue = z.object({ decidedOn: IsoDate }).strict();
export const EnforcementActionWithdraw = z.object({ reason: z.string().trim().min(3).max(2000) }).strict();
/** Closes an issued action with its outcome. */
export const EnforcementActionClose = z.object({ outcome: EnforcementCloseOutcome, reason: z.string().trim().min(3).max(2000) }).strict();
/** A CER user who can decide enforcement actions. */
export const DecisionMaker = z.object({ id: z.string(), username: z.string(), displayName: z.string() });
export const DecisionMakerList = z.object({ items: z.array(DecisionMaker) });
export const ComplianceCase = CaseSummary.extend({
    /** Summary of the concern the case investigates. @metadata business @source compliance.cases.summary */
    summary: z.string(),
    /** Reason recorded with the outcome. @metadata business @source compliance.cases.outcome_reason */
    outcomeReason: z.string().nullable(),
    /** CER officer who opened the case. @metadata technical @source iam.users.display_name via compliance.cases.opened_by_user_id @transform left join */
    openedByName: z.string().nullable(),
    /** Risk signals and compliance records linked to the case. @metadata business @source derived: compliance.case_links rows for the case */
    links: z.array(CaseLink),
    /** Inspections in the case. @metadata business @source derived: compliance.inspections rows for the case */
    inspections: z.array(Inspection),
    /** Audits in the case. @metadata business @source derived: compliance.audits rows for the case */
    audits: z.array(Audit),
    /** Enforcement actions in the case. @metadata business @source derived: compliance.enforcement_actions rows for the case */
    enforcementActions: z.array(EnforcementAction),
    /** The case's append-only trail of steps, oldest first. @metadata business @source derived: compliance.case_events rows for the case @transform participant scope sees only shared events */
    events: z.array(CaseEvent),
});
export const CaseList = z.object({ items: z.array(CaseSummary), slaBusinessDays: z.number().int() });
export const CaseQuery = z.object({
    status: CaseStatus.optional(),
    participantId: z.string().optional(),
    overdue: z.enum(['true', 'false']).optional(),
});
/** Opens a case on a participant, carrying in live risk signals from its risk-view row. */
export const CaseOpen = z
    .object({
    subjectParticipantId: z.string().min(1),
    subjectUnitId: z.string().min(1).optional(),
    /** A CER officer or administrator (see `GET /compliance/cases/owners`). */
    ownerUserId: z.string().uuid(),
    title: z.string().trim().min(3).max(200),
    summary: z.string().trim().min(10).max(5000),
    signals: z.array(z.object({ source: RiskSource, sourceId: z.string().min(1) }).strict()).max(200).default([]),
})
    .strict();
/** Hands the case to another CER officer; appended to the timeline as a CER-only `owner` event. */
export const CaseReassign = z.object({ ownerUserId: z.string().uuid(), reason: z.string().trim().min(3).max(500) }).strict();
/** CER staff who can own a case (active users holding `cases:manage`). */
export const CaseOwner = z.object({ id: z.string(), displayName: z.string(), username: z.string() });
export const CaseOwnerList = z.object({ items: z.array(CaseOwner) });
export const CaseUnitRelation = z.enum(['owner', 'manufacturer', 'installer']);
/** A unit a case on this participant may be about: one it owns, manufactures or installed. */
export const CaseSubjectUnit = z.object({ id: z.string(), serial: z.string(), modelNumber: z.string(), status: z.string(), relations: z.array(CaseUnitRelation) });
export const CaseSubjectUnitList = z.object({ items: z.array(CaseSubjectUnit) });
export const CaseSubjectUnitQuery = z.object({ participantId: z.string().min(1) });
export const CaseStatusChange = z.object({ status: z.enum(['assessing', 'acting']), note: z.string().trim().min(3).max(5000) }).strict();
export const CaseClose = z.object({ outcome: CaseOutcome, reason: z.string().trim().min(3).max(5000) }).strict();
/** CER note; `shared` makes it visible to the subject participant. */
export const CaseNote = z.object({ note: z.string().trim().min(1).max(5000), shared: z.boolean().default(false) }).strict();
export const CaseAcknowledge = z.object({ note: z.string().trim().max(5000).optional() }).strict();
export const CaseEvidence = z.object({ note: z.string().trim().min(3).max(5000), evidenceRef: z.string().trim().min(1).max(500) }).strict();
/** Corrects a note or evidence entry by appending a new event; the original stays on the timeline. */
export const CaseCorrection = z
    .object({
    correctsEventId: z.number().int().positive(),
    note: z.string().trim().min(1).max(5000),
    evidenceRef: z.string().trim().min(1).max(500).optional(),
    reason: z.string().trim().min(3).max(500),
})
    .strict();
// ---- Activity report (compliance demo C9) -----------------------------------
/** One kind of compliance step counted by the activity report; each is written with exactly one audit event. */
export const ReportActivity = z.enum([
    'monitoring_run',
    'case_opened',
    'case_closed',
    'inspection_scheduled',
    'inspection_completed',
    'inspection_cancelled',
    'audit_planned',
    'audit_conducted',
    'audit_closed',
    'audit_cancelled',
    'follow_up_completed',
    'enforcement_drafted',
    'enforcement_issued',
    'enforcement_withdrawn',
    'enforcement_closed',
]);
export const ReportQuery = z.object({ from: IsoDate, to: IsoDate });
export const ReportRecordKind = z.enum(['monitoring_run', 'case', 'inspection', 'audit', 'follow_up', 'enforcement_action']);
/** One step, read from the compliance record it changed. */
export const ReportRow = z.object({
    activity: ReportActivity,
    /** Breakdown within the activity: case close outcome, inspection outcome, enforcement action type. */
    subtype: z.string().nullable(),
    /** Calendar date of `occurredAt` in Sydney time. */
    day: z.string(),
    occurredAt: z.string(),
    /** Null for register-wide steps (monitoring runs). */
    participantId: z.string().nullable(),
    participantName: z.string().nullable(),
    record: z.object({ kind: ReportRecordKind, id: z.string(), label: z.string() }),
    caseId: z.string().nullable(),
    /** The audit event that recorded this step in the period; null if there is none. */
    auditEventId: z.number().int().nullable(),
    /** Who did it, from that audit event. */
    actorName: z.string().nullable(),
});
/** Report rows of one activity checked against `audit.events` for its action in the same period, matched by record id. */
export const ReportReconciliation = z.object({
    activity: ReportActivity,
    auditAction: z.string(),
    reportCount: z.number().int(),
    auditCount: z.number().int(),
    /** Ids of records with a report row but no audit event in the period. */
    missingAudit: z.array(z.string()),
    /** Audit events in the period with no report row (the record was deleted, or the step fell either side of a period boundary). */
    unmatchedAudit: z.array(z.object({ auditEventId: z.number().int(), entityId: z.string().nullable() })),
    matches: z.boolean(),
});
export const ComplianceReport = z.object({
    from: z.string(),
    to: z.string(),
    timeZone: z.string(),
    generatedAt: z.string(),
    total: z.number().int(),
    auditTotal: z.number().int(),
    byActivity: z.array(z.object({ activity: ReportActivity, count: z.number().int(), subtypes: z.array(z.object({ subtype: z.string(), count: z.number().int() })) })),
    byParticipant: z.array(z.object({ participantId: z.string().nullable(), participantName: z.string().nullable(), count: z.number().int() })),
    byDay: z.array(z.object({ day: z.string(), count: z.number().int() })),
    rows: z.array(ReportRow),
    reconciliation: z.array(ReportReconciliation),
    /** Hits retained by the period's monitoring runs against the hit counts their audit events recorded. */
    monitoringHits: z.object({ reportCount: z.number().int(), auditCount: z.number().int(), matches: z.boolean() }),
    /** Cases in the register now, by status (not limited to the period). */
    casesByStatus: z.object({ open: z.number().int(), assessing: z.number().int(), acting: z.number().int(), closed: z.number().int() }),
});
//# sourceMappingURL=compliance.js.map